/**
 * ForceSyncButton — clickable "wake the DPC and pull state" trigger.
 *
 * Pushes an FCM data message {kind:"snapshot+pull"} to the device's
 * registered fcmToken (POST /devices/:id/sync). DPC schedules a one-
 * shot SnapshotPoster on receipt, which posts a fresh snapshot AND
 * pulls pending commands as a side effect.
 *
 * Use when:
 *   - Last snapshot is older than expected — confirm policy state
 *     without waiting up to 15 min for the next periodic tick
 *   - A queued lock command isn't dispatching (FCM token rotation,
 *     queue backlog) and operator wants to retry the wake-up
 *   - Right after editing restrictions in admin-web, to surface the
 *     change on the device without waiting for the periodic refetch
 *
 * Idempotent at the device end — multiple rapid clicks collapse into
 * the same SnapshotPoster scheduling. We add a 5s post-click cooldown
 * locally to keep the audit log clean of spam-clicks.
 *
 * Permission-gated by the parent (`device.sync`); 400 surfaces a
 * "no FCM token" toast — DPC must register first, and the next
 * periodic snapshot will self-heal within 15 min.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "./Button.js";
import { api } from "../lib/api.js";

interface ForceSyncResponse {
  readonly messageId: string;
  readonly sentAt: string;
}

const COOLDOWN_MS = 5_000;

export function ForceSyncButton({ deviceId }: { readonly deviceId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onCooldown, setOnCooldown] = useState(false);

  const sync = useMutation({
    mutationFn: () => api.post<ForceSyncResponse>(`/api/devices/${deviceId}/sync`, {}),
    onSuccess: (res) => {
      setLastResult(`Sync nudge sent at ${new Date(res.sentAt).toLocaleTimeString()}`);
      setError(null);
      setOnCooldown(true);
      // Invalidate the queries that should refresh once the DPC's
      // snapshot lands (≤30s in practice on a healthy network). The
      // server processes the snapshot synchronously, so by the time
      // the cooldown lifts the data will be ready.
      void queryClient.invalidateQueries({ queryKey: ["devices", "onboarding", deviceId] });
      void queryClient.invalidateQueries({ queryKey: ["devices", "byId", deviceId] });
      void queryClient.invalidateQueries({ queryKey: ["devices", "lockEvents", deviceId] });
    },
    onError: (err: unknown) => {
      setLastResult(null);
      const e = err as { status?: number; problem?: { detail?: string } };
      if (e.status === 400) {
        setError(
          "Device has no FCM token registered yet — DPC must check in once before " +
            "force-sync works. The next periodic snapshot (≤15 min) will self-heal.",
        );
      } else {
        setError(e.problem?.detail ?? "Force-sync failed.");
      }
    },
  });

  useEffect(() => {
    if (!onCooldown) return;
    const t = window.setTimeout(() => setOnCooldown(false), COOLDOWN_MS);
    return () => window.clearTimeout(t);
  }, [onCooldown]);

  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="flex items-center gap-3">
        <Button
          onClick={() => sync.mutate()}
          disabled={sync.isPending || onCooldown}
          variant="secondary"
        >
          {sync.isPending ? "Sending…" : onCooldown ? "Cooling down…" : "Force sync now"}
        </Button>
        {lastResult !== null ? (
          <span className="text-xs text-emerald-700">{lastResult}</span>
        ) : null}
        {error !== null ? <span className="text-xs text-red-700">{error}</span> : null}
      </div>
      <p className="text-xs text-slate-500">
        Pushes an FCM nudge to the DPC; it posts a fresh snapshot + pulls pending commands within
        ~5s of receipt. Idempotent — onboarding-status auto-refreshes once the snapshot lands.
      </p>
    </div>
  );
}
